/** Segment id for holdings outside the top N in the equity concentration chart. */
export const STOCK_EQUITY_OTHER_ID = "__other__";

const DEFAULT_TOP_N = 10;

/**
 * Portfolio concentration by equity per stock (top N + Other).
 * @param {Array<{ symbol?: string|null, name?: string|null, equity?: number|string|null }>} stocks
 * @param {{ topN?: number }} [options]
 */
export function buildStockEquityConcentrationBreakdown(stocks, options = {}) {
  const topN = Number.isFinite(options.topN) && options.topN > 0 ? options.topN : DEFAULT_TOP_N;
  /** @type {Array<{ id: string, symbol: string, name: string, equity: number }>} */
  const withEquity = [];

  let missing = 0;

  for (const stock of stocks || []) {
    const symbol = stock?.symbol != null ? String(stock.symbol).trim().toUpperCase() : "";
    if (!symbol) {
      missing += 1;
      continue;
    }
    const equity = Number(stock?.equity);
    if (!Number.isFinite(equity) || equity <= 0) {
      missing += 1;
      continue;
    }
    const name = stock?.name != null ? String(stock.name).trim() : "";
    withEquity.push({ id: symbol, symbol, name, equity });
  }

  withEquity.sort((a, b) => b.equity - a.equity);

  const totalInvestment = withEquity.reduce((sum, r) => sum + r.equity, 0);
  const top = withEquity.slice(0, topN);
  const rest = withEquity.slice(topN);

  /** @type {Array<{ id: string, label: string, rangeLabel: string, count: number, investment: number }>} */
  const segments = top.map((r) => ({
    id: r.symbol,
    label: r.symbol,
    rangeLabel: r.name || "—",
    count: 1,
    investment: r.equity,
  }));

  if (rest.length > 0) {
    const otherInvestment = rest.reduce((sum, r) => sum + r.equity, 0);
    segments.push({
      id: STOCK_EQUITY_OTHER_ID,
      label: `Other (${rest.length} stocks)`,
      rangeLabel: "Remaining holdings",
      count: rest.length,
      investment: otherInvestment,
    });
  }

  const total = withEquity.length;

  return {
    missing,
    total,
    totalInvestment,
    segments: segments.map((s) => {
      const sharePct = totalInvestment > 0 ? (s.investment / totalInvestment) * 100 : 0;
      return {
        ...s,
        pct: sharePct,
        investmentPct: sharePct,
      };
    }),
  };
}

/**
 * @param {string|null|undefined} segmentId
 * @returns {boolean}
 */
export function isStockEquityDrillable(segmentId) {
  return segmentId != null && segmentId !== "" && segmentId !== STOCK_EQUITY_OTHER_ID;
}
